import {
  TextNode,
  SerializedTextNode,
  LexicalNode,
  NodeKey,
  $applyNodeReplacement,
  EditorConfig,
  Spread,
} from 'lexical';

/**
 * Lexical node for an "@mention" of a WA contact / group participant.
 *
 * Holds two pieces of state beyond the displayed text:
 *   __id      — the full WA JID, e.g. "972526847417@c.us". This is what
 *               the engine puts into `mentionedJidList` so the recipient
 *               actually gets a ping notification.
 *   __display — the human-facing name shown inside the chip.
 *
 * Serialized markdown form: `@[Display](id@c.us)`. The send-time pipeline
 * (`bt-common prepareSend` in extension/wa-communication-layer/
 * message/message-utils.ts) already recognises this exact pattern, splits
 * out the JIDs into `mentionedJidList`, and rewrites the body text to
 * `@<phone>` — so on the wire the recipient sees a real WhatsApp mention.
 *
 * `getTextContent()` returns the markdown form on purpose: when the
 * editor state is read for serialization, walking children and reading
 * `child.getTextContent()` naturally produces `@[Name](id)` for a
 * MentionNode without the serializer needing to know about it. The
 * serializer still gets a dedicated case for clarity, but the
 * `getTextContent()` fallback in the loop keeps copy-paste / quote /
 * list-item paths correct without special-casing every container.
 */
export type SerializedMentionNode = Spread<
  {
    type: 'mention';
    mentionId: string;
    mentionDisplay: string;
  },
  SerializedTextNode
>;

const CHIP_CLASS = 'editor-mention-chip';

export class MentionNode extends TextNode {
  __id: string;
  __display: string;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__id, node.__display, node.__key);
  }

  constructor(id: string, display: string, key?: NodeKey) {
    // The `text` we hand to the underlying TextNode is the markdown
    // form. That way Lexical's own getTextContent / copy-paste / range
    // operations see one token of text equal to `@[Name](id)`, which
    // is what the serializer (and the WA send pipeline) expect.
    super(`@[${display}](${id})`, key);
    this.__id = id;
    this.__display = display;
  }

  getId(): string {
    return this.__id;
  }

  getDisplay(): string {
    return this.__display;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    span.className = CHIP_CLASS;
    span.setAttribute('data-lexical-mention', 'true');
    span.setAttribute('data-mention-id', this.__id);
    // Render the visible text WITHOUT the markdown brackets so the
    // user sees a normal "@Name" chip. The underlying __text still
    // holds the markdown form for serialization.
    span.textContent = `@${this.__display}`;
    span.setAttribute('spellcheck', 'false');
    return span;
  }

  updateDOM(prevNode: MentionNode, dom: HTMLElement): boolean {
    if (prevNode.__display !== this.__display || prevNode.__id !== this.__id) {
      dom.textContent = `@${this.__display}`;
      dom.setAttribute('data-mention-id', this.__id);
    }
    // Return false: we manage the DOM ourselves.
    return false;
  }

  // Mentions are inserted/deleted atomically. `isToken` makes Lexical
  // treat the node as a single unit — backspace removes the whole chip
  // instead of letting the caret enter and edit the chars inside.
  isToken(): boolean {
    return true;
  }

  // Treat the chip as a single uneditable segment for caret traversal.
  canInsertTextBefore(): boolean {
    return true;
  }
  canInsertTextAfter(): boolean {
    return true;
  }

  static importJSON(serialized: SerializedMentionNode): MentionNode {
    const node = $createMentionNode({
      id: serialized.mentionId,
      display: serialized.mentionDisplay,
    });
    node.setFormat(serialized.format);
    node.setDetail(serialized.detail);
    node.setMode(serialized.mode);
    node.setStyle(serialized.style);
    return node;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      type: 'mention',
      mentionId: this.__id,
      mentionDisplay: this.__display,
    };
  }
}

export function $createMentionNode(params: {
  id: string;
  display: string;
}): MentionNode {
  return $applyNodeReplacement(new MentionNode(params.id, params.display));
}

export function $isMentionNode(
  node: LexicalNode | null | undefined,
): node is MentionNode {
  return node instanceof MentionNode;
}
